import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { playNotificationSound } from '@/hooks/useUserPreferences';
import { toast } from 'sonner';
import {
  EmailIcon, 
  ResponsesIcon, 
  GlobeIcon, 
  UserPlusIcon, 
  ApiIcon, 
  PlugIcon,
  TicketsIcon,
  type LucideIcon 
} from '@/config/icons';

interface NewTicketPayload {
  id: string;
  subject: string;
  customer_email: string;
  customer_name: string | null;
  workspace_id: string;
  created_at: string;
  metadata: { source?: string } | null;
}

interface SourceConfig {
  label: string;
  icon: LucideIcon;
  colorClass: string;
}

const sourceConfigs: Record<string, SourceConfig> = {
  email: { label: 'Email', icon: EmailIcon, colorClass: 'text-blue-500' },
  widget: { label: 'Widget', icon: ResponsesIcon, colorClass: 'text-purple-500' },
  public_page: { label: 'Formulaire', icon: GlobeIcon, colorClass: 'text-green-500' },
  manual: { label: 'Manuel', icon: UserPlusIcon, colorClass: 'text-gray-500' },
  api: { label: 'API', icon: ApiIcon, colorClass: 'text-orange-500' },
  mcp: { label: 'MCP', icon: PlugIcon, colorClass: 'text-cyan-500' },
};

const defaultSourceConfig: SourceConfig = {
  label: 'Ticket',
  icon: TicketsIcon,
  colorClass: 'text-muted-foreground',
};

function getSourceConfig(source?: string): SourceConfig {
  if (!source) return defaultSourceConfig;
  return sourceConfigs[source] || defaultSourceConfig;
}

export function useTicketNotifications() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { activeWorkspace } = useWorkspaceContext();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const showNotification = useCallback((ticket: NewTicketPayload) => {
    const customerName = ticket.customer_name || ticket.customer_email.split('@')[0];
    const source = (ticket.metadata as { source?: string })?.source;
    const sourceConfig = getSourceConfig(source);
    const Icon = sourceConfig.icon;
    
    // Build description with source
    const description = source 
      ? `${ticket.subject} • via ${sourceConfig.label}`
      : ticket.subject;
    
    toast.info(
      `Nouveau ticket de ${customerName}`,
      {
        description,
        icon: <Icon className={`h-4 w-4 ${sourceConfig.colorClass}`} />,
        action: {
          label: 'Voir',
          onClick: () => {
            window.location.href = `/tickets/${ticket.id}`;
          },
        },
        duration: 8000,
      }
    );

    // Play notification sound if enabled in user preferences
    playNotificationSound();
  }, []);

  useEffect(() => {
    // Don't subscribe if:
    // - No user logged in
    // - No active workspace
    // - Notifications are disabled
    if (!user || !activeWorkspace?.id) {
      return;
    }

    // Check if notifications are enabled for this user
    const notificationsEnabled = (profile as any)?.notify_new_tickets !== false;
    if (!notificationsEnabled) {
      return;
    }

    // Clean up previous channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Subscribe to new tickets in the active workspace
    const channel = supabase
      .channel(`workspace-tickets-${activeWorkspace.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tickets',
          filter: `workspace_id=eq.${activeWorkspace.id}`,
        },
        (payload) => {
          const newTicket = payload.new as NewTicketPayload;
          showNotification(newTicket);
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, activeWorkspace?.id, profile, showNotification]);

  return null;
}
